<template>
  <!-- Non-modal and attached to the admin page (like EditArea) so the 3D viewer beside it stays usable. -->
  <v-dialog
    v-model="visible"
    max-width="760"
    scrollable
    persistent
    hide-overlay
    no-click-animation
    attach="#admin-zones-container">
    <v-card v-if="form">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        {{ form.id ? 'Edit zone' : 'New zone' }}
        <v-spacer />
        <v-btn icon small title="Close" aria-label="Close" @click="visible = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-select
              v-if="!form.id"
              v-model="ownerKey"
              :items="ownerItems"
              label="Belongs to"
              outlined
              dense
              :rules="[required]"
              :hint="ownerHint"
              persistent-hint
              class="mb-3">
              <template v-slot:item="{ item }">
                <v-icon small class="mr-2">{{ item.icon }}</v-icon>{{ item.text }}
              </template>
              <template v-slot:selection="{ item }">
                <v-icon small class="mr-2">{{ item.icon }}</v-icon>{{ item.text }}
              </template>
            </v-select>
            <div v-else class="zone-owner d-flex align-center mb-3">
              <v-icon small class="mr-2">{{ form.facilityGroupId ? 'mdi-group' : 'mdi-domain' }}</v-icon>
              <span>
                {{ form.facilityGroupId ? 'Shared by the facilities in' : 'Belongs to' }}
                <strong>{{ form.scopeName || 'its owner' }}</strong>
              </span>
            </div>
            <v-text-field v-model="form.name" label="Name" outlined dense :rules="[required]" />
            <v-select
              v-model="form.zoneTypeId"
              :items="zoneTypes"
              item-text="name"
              item-value="id"
              label="Zone type"
              outlined
              dense
              :rules="[required]" />
            <v-textarea v-model="form.description" label="Description" outlined dense rows="5" auto-grow />

            <div class="caption mb-1">Colour</div>
            <div class="d-flex flex-wrap mb-4" style="gap: 6px">
              <button
                v-for="colour in palette"
                :key="colour"
                type="button"
                class="swatch"
                :class="{ active: form.colour === colour }"
                :style="{ backgroundColor: colour }"
                :aria-label="'Colour ' + colour"
                @click="form.colour = colour" />
              <!-- Custom colour picked from "More colours" -->
              <button
                v-if="isCustomColour"
                type="button"
                class="swatch active"
                :style="{ backgroundColor: form.colour }"
                :aria-label="'Custom colour ' + form.colour" />
              <v-menu v-model="colourMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="swatch more-colours"
                    title="More colours"
                    aria-label="More colours"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon small>mdi-palette-outline</v-icon>
                  </button>
                </template>
                <v-card>
                  <v-color-picker v-model="pickerColour" mode="hexa" hide-mode-switch show-swatches swatches-max-height="120" />
                  <v-card-actions>
                    <v-spacer />
                    <v-btn small text @click="colourMenu = false">Cancel</v-btn>
                    <v-btn small color="primary" @click="applyPickerColour">Use colour</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </div>

            <!-- Optional: the zone stays active until this day, then retires (links are kept) -->
            <div v-if="form.status === 'retired'" class="retired-note d-flex align-center mb-4">
              <v-icon small class="mr-2">mdi-archive-outline</v-icon>
              Retired on {{ formatDate(form.retiredOn) }}. Reactivate it from the zones list.
            </div>
            <v-menu
              v-else
              v-model="retireMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="form.retireOn ? formatDate(form.retireOn) : ''"
                  label="Retirement date (optional)"
                  prepend-inner-icon="mdi-calendar-clock"
                  readonly
                  clearable
                  outlined
                  dense
                  hint="Stays active until this date, then retires. Its links are kept."
                  persistent-hint
                  class="mb-3"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="form.retireOn = null" />
              </template>
              <v-date-picker v-model="form.retireOn" :min="tomorrow" no-title @input="retireMenu = false" />
            </v-menu>

            <v-expansion-panels v-if="typeAttributes.length" v-model="attributesPanel" flat class="attributes-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="px-3 py-2">
                  <span>
                    Custom attributes
                    <span class="caption ml-1">({{ attributesSetCount }} of {{ typeAttributes.length }} set)</span>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="attributes-scroll pt-2">
                    <template v-for="field in typeAttributes">
                      <v-select
                        v-if="field.fieldType === 'dropdown'"
                        :key="field.id"
                        v-model="form.attributes[field.id]"
                        :items="field.options.map((o) => o.value)"
                        :label="field.label"
                        outlined
                        dense
                        clearable />
                      <v-text-field
                        v-else
                        :key="field.id"
                        v-model="form.attributes[field.id]"
                        :label="field.label"
                        maxlength="500"
                        outlined
                        dense
                        clearable />
                    </template>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12" md="6">
            <v-switch v-model="hasBoundary" inset dense hide-details class="mt-0 mb-3" label="Spatial boundary" />
            <p v-if="!hasBoundary" class="caption">
              The zone can be used for assignments without a boundary. Add one later if needed.
            </p>
            <template v-else>
              <div class="caption mb-2">Boundary extent (world metres)</div>
              <p v-if="!canDraw" class="caption mb-3">
                Drawing on a level plan opens from a facility's admin page (Zones). Here you can type the extent.
              </p>
              <div v-if="canDraw" class="pick-actions d-flex mb-3">
                <template v-if="!pickMode">
                  <v-btn
                    v-if="show3dPick"
                    small
                    outlined
                    color="primary"
                    class="pick-btn"
                    title="Select the boundary on the 3D model"
                    :loading="pickStarting === '3d'"
                    @click="startPick3d">
                    <v-icon small left>mdi-crop</v-icon>Select on 3D model
                  </v-btn>
                  <v-menu offset-y max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        class="pick-btn"
                        title="Draw the boundary on a 2D level plan"
                        :loading="pickStarting === '2d'"
                        v-bind="attrs"
                        v-on="on">
                        <v-icon small left>mdi-floor-plan</v-icon>Draw on 2D plan
                      </v-btn>
                    </template>
                    <v-list dense class="plan-levels-list">
                      <v-subheader>Choose a level plan</v-subheader>
                      <v-list-item
                        v-for="level in planLevels"
                        :key="level.id"
                        :disabled="!!level.unavailable"
                        @click="startPick2d(level.source)">
                        <v-list-item-avatar tile size="36" class="rounded">
                          <v-img v-if="level.thumbnail" :src="level.thumbnail" contain />
                          <v-icon v-else small>mdi-floor-plan</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>{{ level.name }}</v-list-item-title>
                          <v-list-item-subtitle v-if="level.unavailable">{{ level.unavailable }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item v-if="!planLevels.length" disabled>
                        <v-list-item-content>
                          <v-list-item-title>No levels for this facility</v-list-item-title>
                          <v-list-item-subtitle>Add levels and plans in Levels &amp; Plans</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
                <v-btn v-else small color="primary" class="pick-btn" @click="stopPick">
                  <v-icon small left>mdi-check</v-icon>Done
                </v-btn>
              </div>
              <v-alert v-if="pickMode === '3d'" dense text type="info" class="caption mb-3">
                Drag the crop box handles on the 3D model. The values below follow the box.
              </v-alert>
              <v-alert v-if="pickMode === '2d'" dense text type="info" class="caption mb-3">
                <template v-if="drawState === 'drawing'">
                  Click on the {{ pickLevelName }} plan to add points. Click the first point or press Enter
                  to close the shape. Backspace removes the last point, Esc starts over.
                </template>
                <template v-else>
                  Drag the control points on the {{ pickLevelName }} plan to adjust the shape. X and Y show
                  its extent; Z starts from the level's elevations and stays editable.
                </template>
                <div v-if="drawState === 'closed'" class="mt-1">
                  <v-btn x-small text color="primary" class="px-1" @click="redrawPlanShape">
                    <v-icon x-small left>mdi-vector-polygon</v-icon>Redraw
                  </v-btn>
                </div>
              </v-alert>
              <div v-if="footprint && pickMode !== '2d'" class="d-flex align-center caption mb-2">
                <v-icon x-small class="mr-1">mdi-vector-polygon</v-icon>
                Drawn shape, {{ footprint.length }} points. X and Y show its extent.
                <v-spacer />
                <v-btn x-small text color="primary" title="Replace the drawn shape with the min/max box" @click="useBoxInstead">
                  Use box instead
                </v-btn>
              </div>
              <div v-for="row in boxRows" :key="row.prefix" class="d-flex mb-1" style="gap: 8px">
                <v-text-field
                  v-for="axis in ['X', 'Y', 'Z']"
                  :key="row.prefix + axis"
                  v-model.number="box[row.key + axis]"
                  type="number"
                  :label="`${row.prefix} Point ${axis}`"
                  :rules="[boxRule(axis)]"
                  :disabled="pickMode === '3d' || ((pickMode === '2d' || !!footprint) && axis !== 'Z')"
                  outlined
                  dense />
              </div>
              <p class="caption mb-0">
                Moving the boundary does not change saved assignments.
              </p>
            </template>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="visible = false">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import zonesService from '../services/zones.service';
  import { polygonBounds } from '../utils/zoneGeometry';
  import { getDbIdBBox } from '@/utils/forgeUtils';
  import { viewerToGlobal, assertPersistableUnitScale } from '@/utils/coordinateAuthority';
  import { viewerFor } from '@/platform/viewer';
  import { error } from '@/utils/debugLogger';
  import { ZonePlanDrawTool } from '../viewer/zonePlanDrawTool';
  import { planUnavailableReason, isPlanTranslated } from '../utils/planLevels';
  import dayjs from 'dayjs';

  const PICK_POLL_MS = 250;
  const PLAN_LOAD_TIMEOUT_MS = 30000;
  const round3 = (value) => Math.round(value * 1000) / 1000;
  // A 4-point axis-aligned rectangle is exactly what the min/max fields describe.
  function isAxisRectangle(polygon) {
    if (polygon?.length !== 4) return false;
    const b = polygonBounds(polygon);
    return polygon.every((p) => (p.x === b.minX || p.x === b.maxX) && (p.y === b.minY || p.y === b.maxY));
  }
  const PALETTE = ['#2cc295', '#03624c', '#3377A7', '#547D9A', '#665B9B', '#d2a000', '#FBBD1C', '#b71c1c', '#9e9e9e'];
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const DEFAULT_BOX = { minX: 20, minY: 20, minZ: 0, maxX: 40, maxY: 35, maxZ: 10 };

  // Existing boundaries open as their min/max extent.
  function boxFromBoundary({ polygon, zMin, zMax }) {
    const b = polygonBounds(polygon);
    return { minX: b.minX, minY: b.minY, minZ: zMin, maxX: b.maxX, maxY: b.maxY, maxZ: zMax };
  }

  export default {
    name: 'ZoneEditDialog',
    props: {
      zoneTypes: { type: Array, default: () => [] },
      // The facility the page works in (facility admin). Null on the company admin page, where the
      // owner is picked from every facility and group and boundaries are typed, not drawn.
      facilityId: { type: String, default: null },
      // GET zones/owners: { facilities: [{ id, name, facilityGroupId }], groups: [{ id, name }] }.
      owners: { type: Object, default: () => ({ facilities: [], groups: [] }) },
      // Facility levels (ForgeLevel) from the admin page, for drawing on a 2D plan.
      levels: { type: Array, default: () => [] },
    },
    data() {
      return {
        visible: false,
        saving: false,
        form: null,
        hasBoundary: false,
        palette: PALETTE,
        // Hidden for now; the 3D crop-box selection (startPick3d) stays in place to re-enable.
        show3dPick: false,
        colourMenu: false,
        retireMenu: false,
        // The scheduled retirement the zone had when opened (YYYY-MM-DD), to tell whether it changed.
        savedRetireOn: null,
        pickerColour: PALETTE[0],
        box: { ...DEFAULT_BOX },
        // True once the box holds a real boundary (saved, picked in 3D or drawn on a plan), as
        // opposed to the placeholder a new zone starts with. All values are global metres.
        boxPicked: false,
        // Polygon drawn on a 2D plan (world metres); null = the min/max box is the footprint.
        footprint: null,
        // { levelId, levelName } of the plan the footprint was drawn on; saved as boundary.plan.
        planSource: null,
        drawState: null, // 'drawing' | 'closed' while drawing on a plan
        attributesPanel: null, // collapsed; 0 = open
        pickMode: null, // null | '3d' | '2d'
        pickStarting: null,
        pickLevelName: '',
        pickTimer: null,
        boxRows: [
          { prefix: 'Min', key: 'min' },
          { prefix: 'Max', key: 'max' },
        ],
      };
    },
    computed: {
      // Scheduling is for a future day; retiring today is the list's Retire action.
      tomorrow() {
        return dayjs().add(1, 'day').format('YYYY-MM-DD');
      },
      canDraw() {
        return !!this.facilityId;
      },
      // The page's facility and, when it has one, its group.
      pageFacility() {
        return this.owners.facilities.find((f) => f.id === this.facilityId) || null;
      },
      // "facility:<id>" or "group:<id>": one select for both kinds of owner.
      ownerItems() {
        const facility = (f, text = f.name) => ({ value: `facility:${f.id}`, text, icon: 'mdi-domain' });
        const group = (g, text = g.name) => ({ value: `group:${g.id}`, text, icon: 'mdi-group' });
        if (this.facilityId) {
          const items = [facility({ id: this.facilityId, name: this.pageFacility?.name || '' }, 'This facility')];
          const own = this.owners.groups.find((g) => g.id === this.pageFacility?.facilityGroupId);
          if (own) items.push(group(own, `${own.name} (all its facilities)`));
          return items;
        }
        return [
          ...(this.owners.groups.length ? [{ header: 'Facility groups' }, ...this.owners.groups.map((g) => group(g))] : []),
          { header: 'Facilities' },
          ...this.owners.facilities.map((f) => facility(f)),
        ];
      },
      ownerKey: {
        get() {
          if (this.form?.facilityGroupId) return `group:${this.form.facilityGroupId}`;
          return this.form?.facilityId ? `facility:${this.form.facilityId}` : null;
        },
        set(key) {
          const [kind, id] = (key || '').split(':');
          this.form.facilityGroupId = kind === 'group' ? id : null;
          this.form.facilityId = kind === 'facility' ? id : null;
        },
      },
      ownerHint() {
        if (this.form?.facilityGroupId) return 'Every facility in the group lists the zone. Records are checked in the facility they are assigned from.';
        if (this.form?.facilityId) return 'Only this facility lists the zone.';
        return 'Choose a facility, or a facility group to share the zone with all its facilities.';
      },
      // The saved boundary: the drawn shape, or the min/max box as a 4-point footprint, plus heights.
      boxBoundary() {
        const { minX, minY, minZ, maxX, maxY, maxZ } = this.box;
        return {
          polygon: this.footprint
            ? clone(this.footprint)
            : [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }],
          zMin: minZ,
          zMax: maxZ,
          plan: this.footprint && this.planSource ? { ...this.planSource } : null,
        };
      },
      viewer() {
        return this.$store.state.viewer;
      },
      cropExtension() {
        return this.viewer?.getExtension('Cropping3DExtension');
      },
      attributesSetCount() {
        return this.typeAttributes.filter((attr) => this.form.attributes[attr.id]).length;
      },
      isBoxValid() {
        return ['X', 'Y', 'Z'].every((axis) => this.boxRule(axis)() === true);
      },
      // Same list Levels & Plans shows (store.state.levels), with the same rules for opening a plan.
      // Only levels mapped to the 3D model can convert a plan rectangle to world coordinates.
      planLevels() {
        const levels = this.levels.length ? this.levels : this.$store.state.levels || [];
        return levels.map((level) => ({
          id: level.id,
          name: level.name,
          thumbnail: level.planImageUrl || level.thumbnailUrl || null,
          unavailable: planUnavailableReason(level),
          source: level,
        }));
      },
      // Shown on the shape while drawing on a plan, in place of Measure Area's total.
      planLabel() {
        return this.form?.name?.trim() || 'New zone';
      },
      isCustomColour() {
        return !!this.form?.colour && !PALETTE.includes(this.form.colour);
      },
      typeAttributes() {
        const type = this.zoneTypes.find((t) => t.id === this.form?.zoneTypeId);
        return type?.fields || [];
      },
    },
    methods: {
      required(value) {
        return !!value || 'Required';
      },
      open(zone = null) {
        this.form = zone
          ? clone(zone)
          : {
            name: '',
            description: '',
            zoneTypeId: this.zoneTypes[0]?.id,
            // No owner until the user picks one under "Belongs to".
            facilityId: null,
            facilityGroupId: null,
            colour: PALETTE[0],
            attributes: {},
            boundary: null,
          };
        this.form.attributes = this.form.attributes || {};
        this.form.retireOn = this.form.retireOn || null;
        this.savedRetireOn = this.form.retireOn;
        this.retireMenu = false;
        this.pickerColour = this.form.colour || PALETTE[0];
        this.hasBoundary = !!this.form.boundary;
        this.box = this.form.boundary ? boxFromBoundary(this.form.boundary) : { ...DEFAULT_BOX };
        this.boxPicked = !!this.form.boundary;
        const polygon = this.form.boundary?.polygon;
        this.planSource = this.form.boundary?.plan || null;
        // A shape drawn on a plan stays a drawn shape, even when it happens to be a rectangle.
        this.footprint = polygon && (this.planSource || !isAxisRectangle(polygon)) ? clone(polygon) : null;
        this.attributesPanel = null;
        this.visible = true;
      },
      /**
       * Same flow as Facility Admin > Area (EditArea.startCrop): open the 3D viewer if needed,
       * seed the Cropping3DExtension box from the saved boundary or the selected object, then let
       * the user drag its handles. The box is in viewer space; the fields show world metres.
       */
      async startPick3d() {
        this.stopPick();
        if (!this.viewer) {
          this.$store.state.adminAppContainer?.open3dViewerIfNot();
          this.$dialog.notify.warning('Please wait, model still loading');
          return;
        }
        if (!this.cropExtension) {
          this.$dialog.notify.warning('Please wait, model still loading');
          return;
        }
        this.pickStarting = '3d';
        try {
          const seed = await this.seedCropBox();
          if (!seed) {
            this.$dialog.notify.warning('Please select an object on the model first');
            return;
          }
          assertPersistableUnitScale(); // fail before showing the box if the model unit is unknown
          this.cropExtension.cropBox = seed;
          this.cropExtension.toggleCropbox(true);
          this.hasBoundary = true;
          this.pickMode = '3d';
          this.syncBoxFromCrop();
          this.pickTimer = setInterval(this.syncBoxFromCrop, PICK_POLL_MS);
        } catch (err) {
          error('Failed to start 3D boundary selection', err);
          this.$dialog.notify.error(err.message || 'Could not start 3D selection');
        } finally {
          this.pickStarting = null;
        }
      },
      /**
       * Draw the footprint on a level's 2D plan with the Measure Area interaction (ZonePlanDrawTool),
       * labelled with the zone name and with draggable control points. The level's 2D↔3D mapping
       * (Levels & Plans) converts each point to world metres, so the saved polygon matches 3D.
       */
      async startPick2d(level) {
        this.stopPick();
        const unavailable = planUnavailableReason(level);
        if (unavailable) {
          this.$dialog.notify.warning(`${level.name}: ${unavailable}.`);
          return;
        }
        const mapping = level.mappingViewersPointsInfo;
        this.pickStarting = '2d';
        try {
          if (!(await isPlanTranslated(level))) {
            this.$dialog.notify.warning('Plot plan not yet translated, please wait.');
            return;
          }
          const planViewer = await this.openLevelPlan(level);
          if (level.topElevation > level.bottomElevation && !this.boxPicked) {
            this.box = { ...this.box, minZ: round3(level.bottomElevation), maxZ: round3(level.topElevation) };
          }
          this.hasBoundary = true;
          this.pickLevelName = level.name;
          this.pickMode = '2d';
          const tool = new ZonePlanDrawTool(planViewer, {
            label: this.planLabel,
            colour: this.form.colour,
            points: this.planSeedPoints(mapping),
            onChange: this.onPlanShape,
            onState: (state) => {
              this.drawState = state;
            },
          });
          // Kept off `data` on purpose: viewer objects must not become reactive.
          this.plan2d = { viewer: planViewer, tool, mapping, level };
        } catch (err) {
          error('Failed to start 2D boundary drawing', err);
          this.$dialog.notify.error(err.message || 'Could not open the level plan');
        } finally {
          this.pickStarting = null;
        }
      },
      // Opens the level in the admin page's 2D viewer (same as Levels & Plans) and waits for it.
      async openLevelPlan(level) {
        const current = this.$store.state.planViewer;
        const shown = this.$store.state.adminAppContainer?.shownLevel2D;
        if (shown?.id === level.id && current?.model) return current;
        this.$emit('launch-viewer-2d', level);
        const startedAt = Date.now();
        while (Date.now() - startedAt < PLAN_LOAD_TIMEOUT_MS) {
          const viewer = this.$store.state.planViewer;
          if (viewer && viewer !== current && viewer.model) return viewer;
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
        throw new Error('The level plan took too long to load. Try again.');
      },
      // A boundary already in world metres reopens as editable control points; otherwise draw anew.
      planSeedPoints(mapping) {
        if (!this.boxPicked || !this.isBoxValid) return null;
        const THREE = window.THREE;
        const z = this.box.minZ;
        const points = this.boxBoundary.polygon.map((p) => mapping._3dTo2d(new THREE.Vector3(p.x, p.y, z)));
        return points.every(Boolean) ? points : null;
      },
      // Closed shape on the plan → world-metre footprint; the X/Y fields show its extent.
      onPlanShape(planPoints) {
        const mapping = this.plan2d?.mapping;
        if (!mapping) return;
        const world = planPoints.map((p) => mapping._2dTo3d(p));
        if (!world.every(Boolean)) return;
        this.footprint = world.map((p) => ({ x: round3(p.x), y: round3(p.y) }));
        this.planSource = { levelId: this.plan2d.level.id, levelName: this.plan2d.level.name };
        const b = polygonBounds(this.footprint);
        this.box = { ...this.box, minX: b.minX, minY: b.minY, maxX: b.maxX, maxY: b.maxY };
        this.boxPicked = true;
      },
      useBoxInstead() {
        this.footprint = null;
        this.planSource = null;
      },
      redrawPlanShape() {
        this.plan2d?.tool.restart();
      },
      async seedCropBox() {
        const THREE = window.THREE;
        // A boundary already picked from the model reopens where it was.
        if (this.boxPicked && this.isBoxValid) {
          const { minX, minY, minZ, maxX, maxY, maxZ } = this.box;
          const viewer = viewerFor(this.$store);
          const min = viewer.worldToView({ x: minX, y: minY, z: minZ });
          const max = viewer.worldToView({ x: maxX, y: maxY, z: maxZ });
          if (min && max) return new THREE.Box3(min, max);
        }
        const selection = this.viewer.model?.selector?.getSelection() || [];
        if (!selection.length) return null;
        return getDbIdBBox(selection[0], this.viewer);
      },
      // The crop extension has no change event, so the fields follow the box by polling.
      syncBoxFromCrop() {
        const cropBox = this.cropExtension?.cropBox;
        if (!cropBox) return;
        const { scale } = assertPersistableUnitScale();
        const min = viewerToGlobal(cropBox.min, scale);
        const max = viewerToGlobal(cropBox.max, scale);
        this.box = {
          minX: round3(min.x), minY: round3(min.y), minZ: round3(min.z),
          maxX: round3(max.x), maxY: round3(max.y), maxZ: round3(max.z),
        };
        this.boxPicked = true;
        this.footprint = null; // the 3D box replaces any drawn shape
        this.planSource = null;
      },
      stopPick() {
        if (this.pickTimer) clearInterval(this.pickTimer);
        this.pickTimer = null;
        if (this.pickMode === '3d') {
          this.syncBoxFromCrop();
          this.cropExtension?.toggleCropbox(false);
        }
        if (this.pickMode === '2d' && this.plan2d) {
          if (this.drawState === 'drawing' && this.plan2d.tool.points.length) {
            this.$dialog.notify.warning('The shape was not closed, so the previous boundary is kept.');
          }
          this.plan2d.tool.destroy();
        }
        this.plan2d = null;
        this.pickMode = null;
        this.drawState = null;
      },
      boxRule(axis) {
        return () => {
          const min = this.box[`min${axis}`];
          const max = this.box[`max${axis}`];
          if (typeof min !== 'number' || typeof max !== 'number' || Number.isNaN(min) || Number.isNaN(max)) return 'Required';
          return max > min || `Max ${axis} must be greater than Min ${axis}`;
        };
      },
      applyPickerColour() {
        // The picker returns #RRGGBBAA in hexa mode; zones store an opaque #RRGGBB.
        this.form.colour = this.pickerColour.slice(0, 7);
        this.colourMenu = false;
      },
      formatDate(date) {
        return dayjs(date).format('DD MMM YYYY');
      },
      async save() {
        this.stopPick();
        if (this.hasBoundary && !this.isBoxValid) {
          this.$dialog.notify.error('Each Max point must be greater than its Min point.');
          return;
        }
        this.saving = true;
        try {
          const payload = { ...this.form, boundary: this.hasBoundary ? this.boxBoundary : null };
          delete payload.assignmentCount;
          if (!payload.id && !payload.facilityId && !payload.facilityGroupId) {
            this.$dialog.notify.error('Choose the facility or facility group the zone belongs to.');
            return;
          }
          const { data: saved } = await zonesService.saveZone(payload);
          // Scheduling is its own call: set or move the date, or clear it to cancel the schedule.
          const retireOn = this.form.status === 'retired' ? this.savedRetireOn : this.form.retireOn || null;
          if (retireOn !== this.savedRetireOn) {
            await zonesService.setZoneStatus(saved.id, retireOn ? 'retired' : 'active', { retireOn });
          }
          this.$dialog.notify.success(retireOn && retireOn !== this.savedRetireOn
            ? `Zone "${payload.name}" saved; it retires on ${this.formatDate(retireOn)}`
            : `Zone "${payload.name}" saved`);
          this.visible = false;
          this.$emit('saved');
        } catch (err) {
          this.$dialog.notify.error(err.message);
        } finally {
          this.saving = false;
        }
      },
    },
    watch: {
      visible(value) {
        if (!value) this.stopPick();
      },
      planLabel(value) {
        this.plan2d?.tool.setLabel(value);
      },
      'form.colour'(value) {
        this.plan2d?.tool.setColour(value);
      },
    },
    created() {
      this.plan2d = null; // { viewer, tool, mapping } while drawing on a plan; not reactive
    },
    beforeDestroy() {
      this.stopPick();
    },
  };
</script>

<style scoped lang="scss">
  .retired-note {
    font-size: var(--ds-font-body-sm);
    color: var(--v-text_secondary-base);
  }

  .zone-owner {
    font-size: var(--ds-font-body-sm);
  }

  .attributes-panel {
    border: 1px solid var(--v-bg7-base);
    border-radius: var(--ds-radius-md);
  }

  .plan-levels-list {
    max-height: 360px;
    overflow-y: auto;
  }

  .attributes-scroll {
    max-height: 220px;
    overflow-y: auto;
    padding-right: var(--ds-space-xs);
  }

  // 3D and 2D selection buttons share the row equally so they line up.
  .pick-actions {
    gap: var(--ds-space-sm);
  }

  .pick-btn {
    flex: 1 1 0;
    min-width: 0;
  }

  .swatch {
    width: 24px;
    height: 24px;
    border-radius: var(--ds-radius-full);
    border: 2px solid transparent;
    cursor: pointer;

    &.active {
      border-color: var(--v-text-base);
      box-shadow: 0 0 0 2px var(--v-bg2-base) inset;
    }

    &.more-colours {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: var(--v-bg2-base);
      border: 1px dashed var(--v-grey-base);
    }
  }
</style>
